import { SecureStateType, SnapSize } from '../constants/enums'
import { SECURESTATE } from '../state'
import { getStateUser } from '../state/getState'
import { transparentPNG } from '../utilities/image'

/**
 * Shown by `previewSnapshotUrl` while a snapshot cannot be built yet: the
 * age-gate state is unknown, or the gate is active and the snaps token has
 * not arrived. `state.timers` swaps it for the real URL once either settles.
 */
export const SNAP_PENDING_PLACEHOLDER = '/static/palm-trees-bg.webp'

export const avatarUrl = (id: number | string, size = 150): string => {
    if (!id) {
        return transparentPNG
    }
    const prefix = String(id).slice(0, 3)
    const x = String(size)
    const y = String(size)
    // no image-security token: avatars are screened SFW before they go live,
    // so they are served unrestricted in every region (the www and mobile
    // clients request them the same way). Snapshots still need theirs — see
    // previewSnapshotUrl.
    return `https://img.mfcimg.com/photos2/${prefix}/${id}/avatar.${x}x${y}.jpg`
}

export const getSnapShotDetails = (id: number) => {
    const user = getStateUser(id)
    const {
        isClubShow,
        isGroupShow,
        isPublicShow,
        isPrivateShow,
        isTruePrivate,
    } = user || {}

    const shouldBlur =
        isClubShow || isGroupShow || isPrivateShow || isTruePrivate

    let dir = 'blur/'
    // if (isAway) dir = 'away/'
    if (isTruePrivate) dir = 'trueprivate/'
    if (isPublicShow) dir = ''

    let prefix = 'mfc_'
    if (isGroupShow) prefix = 'grp_s_'
    if (isClubShow) prefix = 'clb_s_'
    if (isPrivateShow || isTruePrivate) prefix = 'pvt_s_'

    // TODO: add snapshot support and c2c room support
    // if (isSnapshot) prefix = 's_'
    // if (isCam2CamRoom) prefix = 'uvm_'

    return { dir, shouldBlur, prefix }
}

export interface SnapShotOptions {
    large?: boolean
    xsmall?: boolean
}

export const previewSnapshotUrl = (
    modelID: number,
    options: SnapShotOptions = {},
) => {
    if (!modelID) return transparentPNG

    const user = getStateUser(modelID)
    if (!user) return transparentPNG

    const { channel, isNGVideo, phase, server } = user
    if (!server || !channel) return transparentPNG

    const { dir, prefix, shouldBlur } = getSnapShotDetails(modelID)

    const path = shouldBlur ? dir : ''
    const { large = false, xsmall = false } = options
    const size = large
        ? SnapSize.large
        : xsmall
          ? SnapSize.small
          : SnapSize.medium
    const resolvedPrefix = prefix || (isNGVideo ? phase + '_' : 'mfc_')
    const now = Date.now()

    let rootPath = 'snapimg'

    const isAgeGateActive =
        SECURESTATE[SecureStateType.AGE_VERIFICATION].ageGateActive
    const snaps = SECURESTATE[SecureStateType.IMAGE_SECURITY_CONFIG].snaps
    // the CDN answers an expired token with a blurred fallback, so a page
    // resumed after its token lapsed falls back as if it had none
    const snapsToken =
        now < (snaps?.usableUntil ?? Infinity) ? snaps?.token : undefined

    if (
        typeof isAgeGateActive === 'undefined' ||
        (isAgeGateActive && !snapsToken)
    ) {
        return SNAP_PENDING_PLACEHOLDER
    }

    if (snapsToken) rootPath = snapsToken

    return (
        'https://snap.mfcimg.com/' +
        `${rootPath}/${server}/${path}${size}/${resolvedPrefix}${channel}` +
        `?no-cache=${now}`
    )
}
